import React from 'react';

import { QRROverageMessage } from '../components';

interface GetQrrOverageMessageInput {
    failedIngestions: IngestionFailure[];
    subscriptionSettings: ChargebeeSubscriptionSettings | null;
}

/**
 * Returns JSX that contains messaging about the most recent statement upload that failed due to a
 * QRR overage error, or null if such a message should not be shown.
 * @param input - {@link GetQrrOverageMessageInput}
 * @returns JSX or null
 */
export const getQrrOverageMessage = ({
    failedIngestions,
    subscriptionSettings,
}: GetQrrOverageMessageInput): JSX.Element | null => {
    const logEventsByStatementId: Record<string, LogEvent[]> = {};

    const qrrOverageFailures = failedIngestions.filter(
        (ingestionFailure: IngestionFailure): boolean =>
            ingestionFailure.logEventFailures.some(
                (logEvent: LogEvent): boolean => {
                    const qrrOverage = logEvent?.detail?.qrr_overage;

                    if (qrrOverage !== undefined && qrrOverage > 0) {
                        const statementId =
                            ingestionFailure.statementView?.statement_id ??
                            null;

                        if (statementId !== null) {
                            if (
                                logEventsByStatementId[statementId] ===
                                undefined
                            ) {
                                logEventsByStatementId[statementId] = [
                                    logEvent,
                                ];
                            } else {
                                logEventsByStatementId[statementId].push(
                                    logEvent,
                                );
                            }
                        }
                    }

                    return true;
                },
            ),
    );

    if (qrrOverageFailures.length === 0) {
        return null;
    }

    // Display the details for the most recent ingestion failure due to QRR overage
    const statementId =
        qrrOverageFailures[qrrOverageFailures.length - 1].statementView
            ?.statement_id ?? null;
    const filename =
        qrrOverageFailures?.[qrrOverageFailures.length - 1].filename ?? null;

    if (statementId && filename) {
        const logEvents = logEventsByStatementId[statementId];
        const qrrCapacity =
            logEvents[logEvents.length - 1]?.detail?.qrr_capacity ?? null;
        const qrrOverage =
            logEvents[logEvents.length - 1]?.detail?.qrr_overage ?? null;
        const settingsRoyaltyCapacity =
            subscriptionSettings?.subscription_royalty_capacity ?? null;

        if (
            qrrCapacity !== null &&
            qrrOverage !== null &&
            settingsRoyaltyCapacity !== null &&
            // If the user has changed their royalty capacity since this error occurred, we do
            // not need to show an overage warning
            qrrCapacity === settingsRoyaltyCapacity
        ) {
            return (
                <QRROverageMessage
                    filename={filename}
                    qrrCapacity={qrrCapacity}
                    qrrOverage={qrrOverage}
                />
            );
        }
    }

    return null;
};
